'use client'

import { useState } from 'react'
import { useRouter } from 'next/navigation'

import { Button } from '@/components/ui/button'
import { Loader2 } from '@/components/ui/icons'
import { enableGhostLibraryWriteMode } from '@/lib/api'
import { useLibraryGhostMode, type LibraryGhostSession } from '@/store/ghostModeStore'

export function LibraryGhostModeBanner({ session }: { session: LibraryGhostSession }) {
  const router = useRouter()
  const exit = useLibraryGhostMode(s => s.exit)
  const setMode = useLibraryGhostMode(s => s.setMode)
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const isWriteMode = session.mode === 'write'

  const enableWriteMode = async () => {
    setSwitching(true)
    setError(null)
    try {
      await enableGhostLibraryWriteMode(session.targetOrgId)
      setMode('write')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to enable write mode')
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div className="flex min-h-8 shrink-0 items-center justify-center gap-3 border-b border-border bg-card px-4 py-1 text-xs text-muted-foreground">
      <span className="min-w-0 truncate">
        Library ghost mode: <strong className="text-foreground">{isWriteMode ? 'Write Mode' : 'Read Only'}</strong> for{' '}
        <strong className="text-foreground">{session.targetOrgName}</strong>.
      </span>
      {error && <span className="max-w-52 truncate text-destructive">{error}</span>}
      {isWriteMode ? (
        <Button
          variant="secondary"
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={() => {
            setError(null)
            setMode('read')
          }}
        >
          Read Only
        </Button>
      ) : (
        <Button
          variant="secondary"
          size="sm"
          className="h-6 px-2 text-[11px]"
          onClick={enableWriteMode}
          disabled={switching}
        >
          {switching && <Loader2 className="h-3 w-3 animate-spin" />}
          Enable Write Mode
        </Button>
      )}
      <Button
        variant="ghost"
        size="sm"
        onClick={() => {
          exit()
          router.push('/backstage/projects')
        }}
        className="h-6 px-2 text-[11px]"
      >
        Exit Ghost Mode
      </Button>
    </div>
  )
}
